import { Eye, FileSignature, RotateCw } from "lucide-react";
import PropTypes from "prop-types";
import Datatable from "@components/datatable";
import LoadingButton from "@components/ui-components/loading-button";
import StatusChip from "@components/ui-components/status-chip";
import { formatDateTime } from "@helpers/date";
import SignaturesCell from "@panel/shared/contract/components/signatures-cell";
import {
    PUBLISHER_CONTRACT_STATUS as STATUS,
    PUBLISHER_CONTRACT_STATUS_LABEL,
    PUBLISHER_CONTRACT_STATUS_TONE
} from "@panel/shared/contract/misc/constants";
import { publisherViewLabelOf } from "@panel/shared/contract/misc/helpers";
import { PUBLISHER_NOTE, PUBLISHER_TIME_COPY, SIGN_COPY, signedSummary } from "../misc/constants";

// When the row last moved: signed, sent, or not sent yet.
const timeOf = (row) => {
    if (row.status === STATUS.SIGNED) return `${PUBLISHER_TIME_COPY.signed} ${row.completedAt ? formatDateTime(row.completedAt) : ""}`;
    if (row.sentAt) return `${PUBLISHER_TIME_COPY.sent} ${formatDateTime(row.sentAt)}`;
    return PUBLISHER_TIME_COPY.notSent;
};

// The per-row action, by where the contract stands. Nothing is offered on a
// contract ops has not sent yet — there is no document to open.
const RowAction = ({ row, onSign, onView }) => {
    if (row.status === STATUS.AWAITING_BRAND || row.status === STATUS.BRAND_SIGN_QUEUED) {
        return (
            <LoadingButton
                color="dark"
                size="xs"
                icon={FileSignature}
                text={SIGN_COPY.review}
                loadingText={SIGN_COPY.signing}
                loading={row.status === STATUS.BRAND_SIGN_QUEUED}
                onClick={() => onSign(row)}
                testId="sign-publisher-contract" />
        );
    }

    if (row.status === STATUS.AWAITING_PUBLISHER || row.status === STATUS.SIGNED) {
        return (
            <LoadingButton
                color="neutral"
                size="xs"
                icon={Eye}
                text={publisherViewLabelOf(row)}
                onClick={() => onView(row)}
                testId="view-publisher-contract" />
        );
    }

    return null;
};

RowAction.propTypes = {
    row   : PropTypes.object.isRequired,
    onSign: PropTypes.func.isRequired,
    onView: PropTypes.func.isRequired
};

// One contract per publisher of this brand. The brand signs each after ops
// has sent it — one by one after reviewing it, or all at once. Paged on the
// server; `counts` covers every publisher, not just the page on screen.
const PublisherContractsTable = ({
    loading,
    publishers = [],
    counts = {},
    pagination,
    handlePageChange,
    handlePerRowsChange,
    onRefresh,
    canSignAll = false,
    signingAll = false,
    onSignAll,
    onSign,
    onView
}) => {
    const columns = [
        {
            Header  : "Publisher",
            accessor: "name",
            Cell    : ({ value, row }) => (
                <div className="min-w-0">
                    <p className="truncate font-medium text-gray-900">{value}</p>
                    {row.email && <p className="mt-0.5 truncate text-xs text-muted">{row.email}</p>}
                </div>
            )
        },
        {
            Header  : "Status",
            accessor: "status",
            Cell    : ({ value }) => (
                <StatusChip tone={PUBLISHER_CONTRACT_STATUS_TONE[value]} label={PUBLISHER_CONTRACT_STATUS_LABEL[value]} />
            )
        },
        {
            Header  : "Signatures",
            accessor: "signatures",
            Cell    : ({ value }) => <SignaturesCell signatures={value} />
        },
        {
            Header  : "Time",
            accessor: "sentAt",
            Cell    : ({ row }) => <span className="whitespace-nowrap text-sm text-muted">{timeOf(row)}</span>
        },
        {
            Header  : "Contract",
            accessor: "contractId",
            Cell    : ({ row }) => (
                <div className="flex justify-end">
                    <RowAction row={row} onSign={onSign} onView={onView} />
                </div>
            )
        }
    ];

    return (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
                <div>
                    <h2 className="text-base font-semibold text-gray-900">Publishers</h2>
                    <p className="mt-0.5 text-xs text-muted">{signedSummary(counts)}</p>
                </div>
                <div className="flex items-center gap-2">
                    <LoadingButton
                        color="neutral"
                        size="sm"
                        icon={RotateCw}
                        text="Refresh"
                        loading={loading}
                        onClick={onRefresh}
                        testId="refresh-publisher-contracts" />
                    <LoadingButton
                        color="dark"
                        size="sm"
                        icon={FileSignature}
                        text={canSignAll ? `${SIGN_COPY.all} (${counts.awaitingBrand})` : SIGN_COPY.all}
                        loadingText={SIGN_COPY.signing}
                        loading={signingAll}
                        disabled={!canSignAll}
                        tooltip={canSignAll ? "" : SIGN_COPY.nothingToSign}
                        onClick={onSignAll}
                        testId="sign-all-publisher-contracts" />
                </div>
            </div>
            <Datatable
                columns={columns}
                data={publishers}
                loading={loading}
                pagination={{ count: pagination.total, page: pagination.page, limit: pagination.limit }}
                handlePageChange={handlePageChange}
                handlePerRowsChange={handlePerRowsChange}
                customNoData={<span className="text-sm text-muted">No publishers are associated with this brand yet.</span>} />
            <p className="border-t border-gray-200 px-5 py-3 text-xs leading-relaxed text-muted">{PUBLISHER_NOTE}</p>
        </section>
    );
};

PublisherContractsTable.propTypes = {
    loading            : PropTypes.bool,
    publishers         : PropTypes.array,
    counts             : PropTypes.object,
    pagination         : PropTypes.shape({ total: PropTypes.number, page: PropTypes.number, limit: PropTypes.number }).isRequired,
    handlePageChange   : PropTypes.func.isRequired,
    handlePerRowsChange: PropTypes.func.isRequired,
    onRefresh          : PropTypes.func.isRequired,
    canSignAll         : PropTypes.bool,
    signingAll         : PropTypes.bool,
    onSignAll          : PropTypes.func.isRequired,
    onSign             : PropTypes.func.isRequired,
    onView             : PropTypes.func.isRequired
};

export default PublisherContractsTable;
